import { store } from '../../index';
import axios from 'axios';
import * as requestType from '../../ressources/glossaires/glossaire_requetes';
import {
  askForPasswordReset,
  authentication,
  authenticationV25,
  checkConnexion,
  checkConnexionV25,
  logout,
  logoutV25,
  requestPasswordChange,
  resetPassword,
  validateCodeVerification,
} from '../api';
import { affichageRetourErreurs, setUrlPathFromDomain } from '../utils/utils';
import * as actionTypes from './actionTypes';
import * as actions from './index';

export const intervalDeTempsDemandeInfoConnexionApresConnexion = Number(
  setUrlPathFromDomain('1200000', '1200000', '1200000', '1200000')
);

const requetesAuthIgnoreesPourReconnexion = new Set([
  requestType.connexion,
  requestType.deconnexion,
  requestType.information_de_connexion,
]);

let demandeInfoConnexionTimeoutId = null;
let intercepteurReconnexionInitialise = false;
let ecouteursVisibiliteInitialises = false;

const annulerProchaineDemandeInformationDeConnexion = () => {
  if (demandeInfoConnexionTimeoutId !== null) {
    clearTimeout(demandeInfoConnexionTimeoutId);
    demandeInfoConnexionTimeoutId = null;
  }
};

const planifierProchaineDemandeInformationDeConnexion = (dispatch) => {
  annulerProchaineDemandeInformationDeConnexion();
  demandeInfoConnexionTimeoutId = setTimeout(() => {
    dispatch(demandeInformationDeConnexion());
  }, intervalDeTempsDemandeInfoConnexionApresConnexion);
};

const extrairePayloadRequete = (config) => {
  const donnees = config?.data;

  if (!donnees) {
    return null;
  }

  if (typeof donnees === 'string') {
    try {
      return JSON.parse(donnees);
    } catch (error) {
      return null;
    }
  }

  return donnees;
};

const doitIgnorerReconnexionAutomatique = (config) => {
  const payload = extrairePayloadRequete(config);
  return requetesAuthIgnoreesPourReconnexion.has(payload?.request_type);
};

const declencherReconnexionAutomatique = () => {
  const state = store.getState();

  if (!state?.authentifie?.identifier) {
    return;
  }

  annulerProchaineDemandeInformationDeConnexion();
  store.dispatch({ type: actionTypes.DECONNEXION_AUTOMATIQUE });
};

const peutVerifierLaConnexion = () => {
  const state = store.getState();

  return (
    state?.authentifie?.identifier ||
    state?.authentifie?.reConnexion ||
    localStorage.getItem('auth') !== null
  );
};

const verifierConnexionAuRetourPremierPlan = () => {
  if (!peutVerifierLaConnexion()) {
    return;
  }

  annulerProchaineDemandeInformationDeConnexion();
  store.dispatch(demandeInformationDeConnexion());
};

const initialiserVerificationAuRetourPremierPlan = () => {
  if (ecouteursVisibiliteInitialises) {
    return;
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      verifierConnexionAuRetourPremierPlan();
    }
  });

  window.addEventListener('focus', verifierConnexionAuRetourPremierPlan);
  ecouteursVisibiliteInitialises = true;
};

const initialiserIntercepteurReconnexionAutomatique = () => {
  if (intercepteurReconnexionInitialise) {
    return;
  }

  axios.interceptors.response.use(
    (response) => {
      const erreurs = response?.data?.errors;

      if (
        response?.data?.state === false &&
        Array.isArray(erreurs) &&
        erreurs.includes(100010) &&
        !doitIgnorerReconnexionAutomatique(response?.config)
      ) {
        declencherReconnexionAutomatique();
      }

      return response;
    },
    (error) => {
      const status = error?.response?.status;
      const erreurs = error?.response?.data?.errors;

      if (
        !doitIgnorerReconnexionAutomatique(error?.config) &&
        (status === 401 ||
          status === 403 ||
          (Array.isArray(erreurs) && erreurs.includes(100010)))
      ) {
        declencherReconnexionAutomatique();
      }

      return Promise.reject(error);
    }
  );

  intercepteurReconnexionInitialise = true;
};

initialiserIntercepteurReconnexionAutomatique();
initialiserVerificationAuRetourPremierPlan();

const requestBack = async (dispatch, requestId, payload) => {
  dispatch(actions.debutAttente(requestId));
  try {
    let response;
    let responseV25;
    switch (requestId) {
      case requestType.connexion: {
        // Les deux API (v19 et v25) sont interrogées en parallèle pour n'exiger qu'une seule saisie de mot de passe
        const [reponseV19, reponseV25] = await Promise.all([
          authentication(payload),
          authenticationV25(payload),
        ]);
        response = reponseV19;

        const connexionV19Reussie =
          reponseV19.data.state && reponseV19.data.user_id > 0;
        const connexionV25Reussie =
          reponseV25.data.success && reponseV25.data.user_id;

        if (connexionV19Reussie && connexionV25Reussie) {
          localStorage.setItem(
            'auth',
            JSON.stringify({
              login_name: payload.login_name,
              login_password: await crypto.subtle
                .digest(
                  'SHA-1',
                  new TextEncoder('utf-8').encode(payload.login_password)
                )
                .then((hash) => {
                  return hash;
                }),
            })
          );
          dispatch(enregistrerUtilisateur(reponseV19.data));
          // Summary is fetched by AccueilDashboard on mount, not here, so it only runs when that view actually loads
          planifierProchaineDemandeInformationDeConnexion(dispatch);
          return;
        }

        // Si une seule des deux sessions a pu être ouverte, on la referme pour éviter un état incohérent
        if (connexionV19Reussie && !connexionV25Reussie) {
          await logout().catch((err) => console.error(err));
        } else if (connexionV25Reussie && !connexionV19Reussie) {
          await logoutV25().catch((err) => console.error(err));
        }
        break;
      }

      case requestType.deconnexion: {
        annulerProchaineDemandeInformationDeConnexion();
        const [reponseLogoutV19] = await Promise.all([
          logout(),
          logoutV25().catch((err) => console.error(err)),
        ]);
        response = reponseLogoutV19;
        if (response.data.state) {
          dispatch(actions.deconnexion());
          window.location.href = '/';
          return;
        }
        break;
      }

      case requestType.changer_son_mot_de_passe:
        response = await requestPasswordChange(payload);
        if (response.data.state) {
          dispatch(validationChangementMotDePasse());
          return;
        }
        break;

      case requestType.demande_de_reinitialisation_du_mot_de_passe:
        response = await askForPasswordReset(payload);
        if (response.data.state) {
          dispatch(demandeReinitialisationMDPReussie());
          return;
        }
        break;

      case requestType.validation_du_code_de_verification:
        response = await validateCodeVerification(payload);
        if (response.data.state) {
          dispatch(validationCodeVerificationReussie());
          return;
        }
        break;

      case requestType.reinitialisation_du_mot_de_passe:
        response = await resetPassword(payload);
        if (response.data.state) {
          dispatch(reinitialisationMDPReussie());
          return;
        }
        break;
      default:
        break;
    }
    affichageRetourErreurs(
      dispatch,
      'danger',
      `Erreur dans la requête ${requestId}`,
      response
    );
  } catch (err) {
    console.error('Erreur de connexion:', err);
  } finally {
    dispatch(actions.finAttente(requestId));
  }
};

//[non authentifié] fonction permettant de se connecter
export const authentification = (login_name, login_password) => {
  return (dispatch) => {
    requestBack(dispatch, requestType.connexion, {
      login_name,
      login_password,
    });
  };
};
//[déjà authentifié] Groupe de fonction permettant de gérer les statuts d'authentification
export const demandeInformationDeConnexion = () => {
  /**
   * Fonction récursive pour la requête d'information de connexion
   * @return une fonction dispatch - une fonction pour réaliser des action asynchrone, dispatch aussi des alertes pour agir sur le réducteur alerte.js
   *      tant que l'utilisateur est connécté sur la base de donné fait un appel récursive
   */
  return (dispatch) => {
    const state = store.getState();
    dispatch(actions.debutAttente(requestType.information_de_connexion));
    // Les deux sessions (v19 et v25) sont vérifiées en parallèle, une seule reconnexion étant déclenchée si l'une des deux tombe
    Promise.all([checkConnexion(), checkConnexionV25()])
      .then(([res, resV25]) => {
        if (res?.data.state && resV25?.data.authenticated) {
          planifierProchaineDemandeInformationDeConnexion(dispatch);
          if (state.authentifie.user_id === null) {
            //cas d'une nouvelle page ou du rafraichissement de la page
            //récupération des informations de l'utilisateur connecté
            dispatch(actions.reqRecupererUnUtilisateur(res.data.user_id));
          } else if (
            res.data.user_id !== state.authentifie.user_id ||
            resV25.data.user_id !== state.authentifie.user_id
          ) {
            annulerProchaineDemandeInformationDeConnexion();
            //cas où le user_id d'une des deux sessions ne correspond pas à l'user_id local
            dispatch(actions.deconnexionAutomatique()); //déconnexion partielle si l'utilisateur n'est pas le même que celui connecté sur le serveur
          }
        } else {
          annulerProchaineDemandeInformationDeConnexion();
          dispatch(actions.deconnexionAutomatique()); //déconnexion partielle si l'une des deux sessions (v19 ou v25) n'est plus valide
          affichageRetourErreurs(dispatch, 'danger', res);
        }
      })
      .catch((err) => {
        console.error(err);
        if (peutVerifierLaConnexion()) {
          planifierProchaineDemandeInformationDeConnexion(dispatch);
        }
      })
      .finally(() =>
        dispatch(actions.finAttente(requestType.information_de_connexion))
      );
  };
};

export const seDeconnecter = () => {
  // return {type:actionTypes.RE_CONNEXION, idConnexion:idConnexion, motDePasse:motDePasse};
  return (dispatch) => {
    requestBack(dispatch, requestType.deconnexion);
  };
};

//[déjà authentifié] Groupe de fonction permettant de gérer les informations de l'utilisateur connecté
export const reqChangerMDP = (
  login_password,
  new_password,
  new_pass_confirm
) => {
  return (dispatch) => {
    requestBack(dispatch, requestType.changer_son_mot_de_passe, {
      login_password,
      new_password,
      new_pass_confirm,
    });
  };
};

//[non authentifié] Groupe de fonction permettant de réinitialiser un mot de passe
export const demandeReinitialisationMDP = (email) => {
  return (dispatch) => {
    requestBack(
      dispatch,
      requestType.demande_de_reinitialisation_du_mot_de_passe,
      {
        email,
      }
    );
  };
};

export const validationCodeVerification = (codeVerification) => {
  /**
   * envoie la requête de vérification du code de validation
   */
  return (dispatch) => {
    requestBack(dispatch, requestType.validation_du_code_de_verification, {
      codeVerification,
    });
  };
};

export const reinitialisationMDP = (
  verification_code,
  new_password,
  new_password_confirm
) => {
  return (dispatch) => {
    requestBack(dispatch, requestType.reinitialisation_du_mot_de_passe, {
      verification_code,
      new_password,
      new_password_confirm,
    });
  };
};

//[déjà authentifié] Groupe de fonction permettant de gérer l'interface de l'utilisateur (commutateur interface admin/client, filtre par projet_id)
export const commutateurInterfaceClient = () => {
  return { type: actionTypes.COMMUTER_INTERFACE_CLIENT };
};
export const commutateurInterfaceAdmin = () => {
  return { type: actionTypes.COMMUTER_INTERFACE_ADMIN };
};
export const gererFiltreParProjet = (filtre_projects_list_id) => {
  return {
    type: actionTypes.GERER_FILTRE_PAR_PROJET,
    filtre_projects_list_id: filtre_projects_list_id,
  };
};
export const deconnexionAutomatique = () => {
  annulerProchaineDemandeInformationDeConnexion();
  return { type: actionTypes.DECONNEXION_AUTOMATIQUE };
};
export const deconnexion = () => {
  annulerProchaineDemandeInformationDeConnexion();
  return { type: actionTypes.DECONNEXION };
};
const demandeReinitialisationMDPReussie = () => {
  return { type: actionTypes.DEMANDE_DE_REINITIALISATION_MDP_REUSSIE };
};
const validationCodeVerificationReussie = () => {
  return { type: actionTypes.VALIDATION_CODE_VERIFICATION_REUSSIE };
};
const reinitialisationMDPReussie = () => {
  return { type: actionTypes.REINITIALISATION_MDP_REUSSIE };
};
const validationChangementMotDePasse = () => {
  return { type: actionTypes.VALIDATION_CHANGEMENT_MOT_DE_PASSE };
};
export const enregistrerUtilisateur = (data) => {
  return {
    type: actionTypes.DEBUT_AUTH,
    ...data,
    // user_id: data.user_id,
    // lastname: data.lastname,
    // name: data.name,
    // privileges_list: data.privileges_list,
    // projects_list: data.projects_list,
    // favorite_projects_list_id: data.favorite_projects_list_id,
    // email:data.email,
    // phone_index:data.phone_index,
    // phone_number:data.phone_number,
    // company_name:data.company_name,
    // profil_image:data.profil_image
  };
};
